import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, User, DollarSign, Car, Clock, CheckCircle, XCircle, Loader2, ArrowUpDown, AlertCircle } from 'lucide-react';
import bookingService from '../../services/bookingService';
import { getImageUrl } from '../../utils/imageUtils';

/**
 * AgencyBookings Component (VendorBookingList)
 * Displays the list of bookings for the agency/vendor
 * 
 * Features:
 * - Fetches data from GET /api/bookings/vendor
 * - Bearer Token automatically sent via apiClient for req.user.id identification
 * - Dynamic query params: page, limit, sortBy, sortOrder
 * - Loading and error states
 * - Table display with all booking information
 * - Pagination with Previous/Next buttons
 */
const AgencyBookings = () => {
  const { status: routeStatus } = useParams();

  const statusParamToTab = (statusParam) => {
    if (!statusParam) return 'all';
    const normalized = statusParam.toLowerCase();
    const allowed = [
      'all',
      'pending',
      'confirmed',
      'live',
      'completed',
      'cancelled',
      'declined',
      'expired',
      'refunded',
    ];
    return allowed.includes(normalized) ? normalized : 'all';
  };

  const [activeTab, setActiveTab] = useState(() => statusParamToTab(routeStatus));
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'createdAt', 
    direction: 'desc' 
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  // Mettre à jour l'onglet actif quand l'URL change (depuis le sidebar)
  useEffect(() => {
    setActiveTab(statusParamToTab(routeStatus));
  }, [routeStatus]);

  // Map tab to status filter
  const getStatusFilter = (tab) => {
    const statusMap = {
      all: '',
      pending: 'PENDING',
      confirmed: 'CONFIRMED',
      live: 'LIVE',
      completed: 'COMPLETED',
      cancelled: 'CANCELLED',
      declined: 'DECLINED',
      expired: 'EXPIRED',
      refunded: 'REFUNDED',
    };
    return statusMap[tab] || '';
  };

  // Load bookings - useEffect triggers on component mount and when dependencies change
  const loadBookings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const status = getStatusFilter(activeTab);
      const params = {
        page: currentPage,
        limit: 20,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...(status && { status }),
      };

      // Appel API vers /api/bookings/vendor
      // Le Bearer Token est automatiquement ajouté par apiClient dans les headers
      // Cela permet à Node.js d'identifier req.user.id
      const response = await bookingService.getVendorBookings(params);
      
      if (response.success) {
        setBookings(response.data || []);
        setPagination(response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 20,
        });
      } else {
        setBookings([]);
        setError('Aucune donnée retournée par l\'API');
      }
    } catch (error) {
      console.error('Error loading vendor bookings:', error);
      setError(error.message || 'Erreur lors du chargement des réservations');
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, currentPage, sortConfig]);

  // useEffect déclenche l'appel dès le chargement de la page
  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Reset to page 1 when changing tab
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Handle sort
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page on sort change
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount, currency = 'MAD') => {
    if (!amount) return '0,00 MAD';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        label: 'En attente',
        icon: Clock,
      },
      PAID: { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        label: 'Payé',
        icon: CheckCircle,
      },
      PAID_ON_DELIVERY: { 
        bg: 'bg-blue-100', 
        text: 'text-blue-800', 
        label: 'Paiement à la livraison',
        icon: DollarSign,
      },
      REFUNDED: { 
        bg: 'bg-purple-100', 
        text: 'text-purple-800', 
        label: 'Remboursé',
        icon: XCircle,
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  // Get booking status badge
  const getBookingStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      CONFIRMED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Confirmée' },
      LIVE: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours' },
      COMPLETED: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Terminée' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Annulée' },
      DECLINED: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Refusée' },
      EXPIRED: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Expirée' },
      REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Remboursée' },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Get client name from populated client object
  const getClientName = (client) => {
    if (!client) return 'Client non spécifié';
    
    // Essayer différents champs possibles selon le modèle
    return client.name || 
           client.fullName || 
           `${client.firstName || ''} ${client.lastName || ''}`.trim() ||
           client.email ||
           'Client non spécifié';
  };

  // Filtrage côté front pour garantir le bon résultat même si l'API ignore le param status
  const filteredBookings = React.useMemo(() => {
    const statusFilterValue = getStatusFilter(activeTab);
    if (!statusFilterValue) return bookings;
    return bookings.filter((b) => b.status === statusFilterValue);
  }, [bookings, activeTab]);

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Mes Réservations</h2>
        <p className="text-gray-600">Gérez toutes vos réservations de véhicules</p>
      </div>

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <div>
            <p className="text-red-800 font-medium">Erreur</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Bookings Table */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#27489E] mx-auto mb-4" />
          <p className="text-gray-600">Chargement des réservations...</p>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune réservation trouvée</p>
          {error && (
            <p className="text-red-600 text-sm mt-2">Erreur: {error}</p>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Véhicule
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('dates.start')}
                    >
                      <div className="flex items-center gap-2">
                        Dates
                        <ArrowUpDown className="w-4 h-4" />
                        {sortConfig.key === 'dates.start' && (
                          <span className="text-[#27489E]">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('financials.total')}
                    >
                      <div className="flex items-center gap-2">
                        Total
                        <ArrowUpDown className="w-4 h-4" />
                        {sortConfig.key === 'financials.total' && (
                          <span className="text-[#27489E]">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('financials.vendorNet')}
                    >
                      <div className="flex items-center gap-2">
                        Montant Net
                        <ArrowUpDown className="w-4 h-4" />
                        {sortConfig.key === 'financials.vendorNet' && (
                          <span className="text-[#27489E]">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paiement
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.map((booking) => (
                    <tr key={booking._id} className="hover:bg-gray-50">
                      {/* Vehicle */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {booking.vehicle?.image && (
                            <img
                              src={getImageUrl(booking.vehicle.image)}
                              alt={booking.vehicle?.title || 'Véhicule'}
                              className="w-12 h-12 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/48x48?text=No+Image';
                              }}
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {booking.vehicle?.title || 'Véhicule non spécifié'}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Client */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div className="text-sm text-gray-900">
                            {getClientName(booking.client)}
                          </div>
                        </div>
                      </td>

                      {/* Dates */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div>
                            <div>{formatDate(booking.dates?.start)}</div>
                            <div className="text-xs text-gray-500">→ {formatDate(booking.dates?.end)}</div>
                          </div>
                        </div>
                      </td>

                      {/* Total */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(booking.financials?.total || 0, booking.financials?.currency)}
                      </td>

                      {/* Vendor Net */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                        {formatCurrency(booking.financials?.vendorNet || 0, booking.financials?.currency)}
                      </td>

                      {/* Booking Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getBookingStatusBadge(booking.status)}
                      </td>

                      {/* Payment Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(
                          booking.financials?.status || 
                          booking.paymentStatus || 
                          'PENDING'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  Page {pagination.currentPage} sur {pagination.totalPages} 
                  {' '}({pagination.totalItems} réservations)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                  }}
                  disabled={currentPage === 1 || isLoading}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Précédent
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1));
                  }}
                  disabled={currentPage === pagination.totalPages || isLoading}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgencyBookings;
